/**
 * Constructs a full Supabase Storage URL from a bucket and path.
 */
const SUPABASE_URL = 'https://ovurabtpmbmdcjsahrwe.supabase.co';

export const DEFAULT_PLACEHOLDER = '/placeholder.svg';

export function getStorageUrl(bucket: string, path: string): string {
  return `${SUPABASE_URL}/storage/v1/object/public/${bucket}/${path}`;
}

/**
 * Optimizes Supabase Storage image URLs by appending transformation parameters.
 * For non-Supabase URLs, returns the original URL unchanged.
 * 
 * @param url - The original image URL (can be full URL or relative path)
 * @param width - Desired width in pixels
 * @param height - Desired height in pixels
 * @param serviceType - Optional service type to determine bucket
 * @returns Optimized image URL with transformation parameters
 */
export function getOptimizedImageUrl(
  url: string, 
  width: number, 
  height: number,
  serviceType?: 'dine_in' | 'takeaway' | 'delivery' | 'cloud_kitchen'
): string {
  if (!url) return DEFAULT_PLACEHOLDER;
  
  // If it's already a full URL
  if (url.startsWith('http://') || url.startsWith('https://')) {
    // Check if the URL is from Supabase Storage
    if (url.includes('supabase.co/storage')) {
      // Strip any existing transformation params and apply fresh ones.
      // Supabase public storage URLs have no auth tokens, so stripping the query
      // string is always safe. This prevents duplicate width/height params when
      // a URL has already been through getOptimizedImageUrl once (e.g. from
      // transformDishForCard) and is then resized to a smaller thumbnail size.
      const baseUrl = url.split('?')[0];
      return `${baseUrl}?width=${width}&height=${height}&resize=cover&quality=80`;
    }
    // Return original URL for non-Supabase images
    return url;
  }
  
  // If it's a relative path (e.g., "user_id/filename.jpg")
  // Determine the bucket based on service type
  const bucket = serviceType === 'takeaway' || serviceType === 'delivery' || serviceType === 'cloud_kitchen'
    ? 'product-images'
    : 'dish-images';
  
  // Construct full URL and apply transformations
  const fullUrl = getStorageUrl(bucket, url);
  return `${fullUrl}?width=${width}&height=${height}&resize=cover&quality=80`;
}

/**
 * Optimizes avatar URLs for smaller file sizes.
 * Default size is 80x80 (sufficient for 40px avatars at 2x resolution).
 * 
 * @param url - The avatar URL
 * @param size - The desired size in pixels (default: 80)
 * @returns Optimized avatar URL
 */
export function getOptimizedAvatarUrl(url: string | null | undefined, size: number = 80): string {
  if (!url) return '';
  return getOptimizedImageUrl(url, size, size);
}

/**
 * Normalizes an image URL to ensure it's a full, valid URL.
 * Handles both full URLs and relative paths from Supabase Storage.
 * 
 * @param imageUrl - The image URL or path
 * @param serviceType - The service type to determine correct bucket for relative paths
 * @returns A normalized full URL
 */
export function normalizeImageUrl(
  imageUrl: string | null | undefined,
  serviceType?: 'dine_in' | 'takeaway' | 'delivery' | 'cloud_kitchen'
): string {
  // Default placeholder for missing images
  const placeholder = DEFAULT_PLACEHOLDER;
  
  if (!imageUrl) return placeholder;
  
  // If it's already a full URL, return as-is
  if (imageUrl.startsWith('http://') || imageUrl.startsWith('https://')) {
    return imageUrl;
  }
  
  // If it's a relative path, construct full URL with correct bucket
  const bucket = serviceType === 'takeaway' || serviceType === 'delivery' || serviceType === 'cloud_kitchen'
    ? 'product-images'
    : 'dish-images';
  
  return getStorageUrl(bucket, imageUrl);
}
